import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, Loader2, Lock, Unlock, Archive, Files, Users2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { CURATED_STATUS, pickEditable } from "@/pages/curated/shared";
import { CuratedSettingsTab } from "@/pages/curated/CuratedSettingsTab";
import { CuratedItineraryTab } from "@/pages/curated/CuratedItineraryTab";
import { CuratedHotelsTab } from "@/pages/curated/CuratedHotelsTab";
import { CuratedTextsTab } from "@/pages/curated/CuratedTextsTab";
import { CuratedBookingsTab } from "@/pages/curated/CuratedBookingsTab";
import { CuratedStatsTab } from "@/pages/curated/CuratedStatsTab";

const STATUS_ACTIONS = {
  draft: [{ target: "published", label: "Publicar", icon: Unlock }],
  published: [{ target: "closed", label: "Cerrar reservas", icon: Lock }],
  closed: [{ target: "published", label: "Reabrir", icon: Unlock }],
};

export default function CuratedDetail({ id, onBack }) {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "settings";
  const [trip, setTrip] = useState(null);
  const [saving, setSaving] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const timer = useRef(null);
  const lastSaved = useRef("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/curated/${id}`); setTrip(data); lastSaved.current = JSON.stringify(pickEditable(data)); }
    catch (e) { toast.error(apiErrorMessage(e, "No se pudo cargar el Curated Trip")); onBack(); }
  }, [id, onBack]);
  useEffect(() => { load(); }, [load]);

  const scheduleSave = useCallback((next) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const payload = pickEditable(next);
      const snap = JSON.stringify(payload);
      if (snap === lastSaved.current) return;
      setSaving(true);
      try {
        const { data } = await api.patch(`/curated/${id}`, payload);
        lastSaved.current = JSON.stringify(pickEditable(data));
        setTrip((t) => ({ ...t, ...data }));
      } catch (e) { toast.error(apiErrorMessage(e, "No se pudo guardar")); }
      finally { setSaving(false); }
    }, 800);
  }, [id]);

  const update = (patch) => setTrip((t) => { const next = { ...t, ...(typeof patch === "function" ? patch(t) : patch) }; scheduleSave(next); return next; });
  const setTab = (v) => { const n = new URLSearchParams(sp); n.set("tab", v); setSp(n); };

  const setStatus = async (target) => {
    try { const { data } = await api.patch(`/curated/${id}`, { status: target }); setTrip((t) => ({ ...t, ...data })); toast.success(`Estado: ${CURATED_STATUS[data.status]?.label || data.status}`); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const archive = async () => {
    try { await api.delete(`/curated/${id}`); toast.success("Archivado"); onBack(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const duplicate = async () => {
    try { const { data } = await api.post(`/curated/${id}/duplicate`); toast.success("Duplicado · abriendo la copia"); const n = new URLSearchParams(); n.set("id", data.id); setSp(n); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const publicUrl = trip ? `${window.location.origin}/curated/${trip.public_token}` : "";
  const copyLink = async () => { try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copiado"); } catch { window.prompt("Copia el link:", publicUrl); } };

  if (!trip) return <div className="flex items-center gap-2 p-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>;
  const st = CURATED_STATUS[trip.status] || CURATED_STATUS.draft;

  return (
    <div className="space-y-5" data-testid="curated-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="curated-back-btn" aria-label="Volver"><ArrowLeft className="h-5 w-5" /></Button>
          <div className="min-w-0 flex-1">
            <input value={trip.name} onChange={(e) => update({ name: e.target.value })} data-testid="curated-name-input"
              className="w-full truncate border-none bg-transparent text-2xl font-semibold tracking-tight focus:outline-none focus:ring-0" />
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant={st.variant} data-testid="curated-detail-status">{st.label}</Badge>
              <span className="inline-flex items-center gap-1"><Users2 className="h-3 w-3" /> {trip.seats_committed}/{trip.capacity} plazas · {trip.currency}</span>
              <span>· {trip.duration_days || 0} días</span>
              {saving && <span className="inline-flex items-center gap-1 text-primary"><Loader2 className="h-3 w-3 animate-spin" /> Guardando…</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {trip.status !== "draft" && (<>
            <Button variant="outline" size="sm" onClick={copyLink} data-testid="curated-copy-link"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copiar link</Button>
            <Button variant="outline" size="sm" asChild><a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="curated-view-landing"><ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Ver landing</a></Button>
          </>)}
          {(STATUS_ACTIONS[trip.status] || []).map((a) => (
            <Button key={a.target} size="sm" variant={a.target === "published" ? "default" : "secondary"} onClick={() => setStatus(a.target)} data-testid={`curated-status-btn-${a.target}`}>
              <a.icon className="mr-1.5 h-3.5 w-3.5" /> {a.label}
            </Button>
          ))}
          <Button variant="outline" size="sm" onClick={duplicate} data-testid="curated-duplicate-btn"><Files className="mr-1.5 h-3.5 w-3.5" /> Duplicar</Button>
          <Button variant="ghost" size="icon" onClick={() => setArchiveOpen(true)} data-testid="curated-archive-btn" aria-label="Archivar"><Archive className="h-4 w-4" /></Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="settings" data-testid="curated-tab-settings">Ajustes & precio</TabsTrigger>
          <TabsTrigger value="itinerary" data-testid="curated-tab-itinerary">Itinerario ({(trip.days || []).length})</TabsTrigger>
          <TabsTrigger value="hotels" data-testid="curated-tab-hotels">Hoteles ({(trip.accommodations || []).length})</TabsTrigger>
          <TabsTrigger value="texts" data-testid="curated-tab-texts">Textos del cliente</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="curated-tab-bookings">Reservas ({(trip.bookings || []).length})</TabsTrigger>
          <TabsTrigger value="stats" data-testid="curated-tab-stats">Estadísticas</TabsTrigger>
        </TabsList>
        <TabsContent value="settings"><CuratedSettingsTab trip={trip} onChange={update} /></TabsContent>
        <TabsContent value="itinerary"><CuratedItineraryTab trip={trip} onChange={update} /></TabsContent>
        <TabsContent value="hotels"><CuratedHotelsTab trip={trip} onChange={update} /></TabsContent>
        <TabsContent value="texts"><CuratedTextsTab trip={trip} onChange={update} /></TabsContent>
        <TabsContent value="bookings"><CuratedBookingsTab trip={trip} reload={load} /></TabsContent>
        <TabsContent value="stats"><CuratedStatsTab trip={trip} /></TabsContent>
      </Tabs>

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Archivar «{trip.name}»</AlertDialogTitle>
            <AlertDialogDescription>Se ocultará de la lista y la landing dejará de estar disponible. Bloqueado si hay reservas pagadas.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={archive} data-testid="curated-archive-confirm">Archivar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
